import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Grade 5.
 *
 * `arithmetic.ts`, `fractions.ts`, `decimals.ts` and `algebra.ts` teach the
 * spine Grade 5 shares with the grades either side of it. These lessons are the
 * year's own work, and the year has one argument running through it: **the
 * whole number stops being the unit**.
 *
 * Every lesson below is that argument in a different costume. A digit is a
 * power of ten away from its neighbour, so multiplying by 100 is a move rather
 * than a calculation. A fraction bar is a division sign, so 17 ÷ 5 always had
 * an exact answer. Multiplication can shrink and division can grow, once the
 * second number is allowed below one. And a solid is measured by filling it
 * rather than covering it, which needs a third measurement the plane never
 * asked for.
 *
 * Two things follow from that, and they shape the writing. Nearly every Grade 5
 * error is a *size* error rather than an arithmetic one — the point in the
 * wrong place, the fraction part left larger than a whole, the answer that grew
 * when it should have shrunk — so almost every lesson here asks the student to
 * predict the size of the answer before computing it. And the intuitions being
 * built are the ones Grade 6 spends the year formalising, so each lesson says
 * plainly where it is going.
 */
export const GRADE_FIVE_LESSONS: Lesson[] = [
  /* ------------------------------------------- place value and decimals */
  lesson({
    id: 'powers-of-ten-01',
    unitId: 'unit-powers-of-ten-5',
    conceptId: 'powers-of-ten',
    title: 'Powers of Ten',
    summary: 'Multiplying by ten is not a calculation. It is every digit taking one step to the left.',
    objectives: [
      'Write 10, 100 and 1000 as powers of ten',
      'Multiply and divide a decimal by a power of ten',
      'Say which way the digits move, and why',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'The pattern you already half know',
        blocks: [
          text('Ask most people for 4.7 × 100 and they will say "move the decimal point two places". They are right, and the rule works. But a rule you cannot explain is a rule you will misremember under pressure — usually by moving the point the wrong way.'),
          text('This lesson is about why the digits move at all, and about a shorter way to write the numbers that make them move.'),
        ],
      },
      intuition: {
        title: 'Every place is ten times the one to its right',
        blocks: [
          text('That single fact is what place value has always meant. A hundred is ten tens. A ten is ten ones. And going the other way: a one is ten tenths, and a tenth is ten hundredths.'),
          figure({ kind: 'place-value', digits: '4700', places: ['thousands', 'hundreds', 'tens', 'ones'] }, 'Each column is worth ten of the column on its right.'),
          text('So when you multiply a number by 10, every digit becomes worth ten times as much. There is only one way for a digit to be worth ten times as much: it has to sit one column further left.'),
          insight('The digits move; the point stays where it is. Writing it the other way round — "the point moves" — gives the same answer and is the reason so many students move it in the wrong direction. Ask instead: should this number get bigger or smaller?'),
          text('Multiplying by 100 is multiplying by 10 twice, so the digits take two steps. Dividing by 1000 makes every digit worth a thousandth as much, so they take three steps the other way.'),
        ],
      },
      visual: {
        title: 'Writing the tens more shortly',
        blocks: [
          text('Rather than write 10 × 10 × 10, we write how many tens are being multiplied.'),
          table(
            ['Power', 'Means', 'Value', 'Zeros'],
            [
              ['10^1', '10', '10', '1'],
              ['10^2', '10 × 10', '100', '2'],
              ['10^3', '10 × 10 × 10', '1000', '3'],
              ['10^4', '10 × 10 × 10 × 10', '10 000', '4'],
            ],
          ),
          warning('10^3 is 1000, not 30. The small number counts how many tens are being *multiplied together* — it is not something to multiply the 10 by.'),
          text('The last column is the useful one. The exponent, the number of zeros, and the number of places the digits move are all the same number.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Work out 3.6 × 10^2 and 45.1 ÷ 10^3.'),
          steps([
            { label: 'Read the power.', latex: '10^2 = 100', note: 'Two tens multiplied, so two zeros.' },
            { label: 'Decide the direction first. Multiplying by 100 makes it larger.', latex: '3.6 \\times 100 > 3.6' },
            { label: 'Move every digit two places left.', latex: '3.6 \\times 10^2 = 360' },
            { label: 'Now the division. Dividing by 1000 makes it smaller.', latex: '45.1 \\div 1000 < 45.1' },
            { label: 'Move every digit three places right, filling with zeros.', latex: '45.1 \\div 10^3 = 0.0451' },
          ]),
          tip('Check the size, not the digits. If you divided by a thousand and your answer is bigger than what you started with, no amount of checking the digits will find the mistake — the direction is what went wrong.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('powers-of-ten', 4)] },
      independent: { title: 'On your own', blocks: [practice('powers-of-ten', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('powers-of-ten', 3, 'Find the power of ten that was used.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Each place is worth ten times the place on its right.',
            '10^n means n tens multiplied together, and it has n zeros.',
            'Multiplying moves digits left; dividing moves them right.',
            'Decide whether the answer should be bigger or smaller before you move anything.',
          ]),
          insight('This is the whole of scientific notation, three years early. In Grade 8 you will write 93 000 000 as 9.3 × 10^7, and the only new part will be the size of the exponent.'),
        ],
      },
    },
  }),

  lesson({
    id: 'round-decimals-01',
    unitId: 'unit-powers-of-ten-5',
    conceptId: 'round-decimals',
    title: 'Rounding Decimals',
    summary: 'The rule you know for whole numbers, applied to the places smaller than one — and the trap of applying it twice.',
    objectives: [
      'Round a decimal to the nearest whole, tenth or hundredth',
      'Identify which digit decides the rounding',
      'Explain why rounding in stages gives the wrong answer',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Nothing new, one place further right',
        blocks: [
          text('You already round whole numbers: 3847 to the nearest hundred is 3800, because 47 is less than half of a hundred.'),
          text('Rounding a decimal asks exactly the same question, in a column that happens to be smaller than one. To the nearest tenth, is 2.34 closer to 2.3 or to 2.4?'),
        ],
      },
      intuition: {
        title: 'It is a question about a number line',
        blocks: [
          text('Rounding to the nearest tenth means choosing between the two tenths either side. Draw them and the answer stops being a rule at all.'),
          figure(
            { kind: 'number-line', from: 2.3, to: 2.4, step: 0.05, marks: [{ at: 2.34, label: '2.34' }] },
            'The tick in the middle is halfway, at 2.35. Because 2.34 falls short of it, 2.34 is nearer to 2.3.',
          ),
          insight('Only one digit decides: the one immediately to the right of the place you are rounding to. It is the digit that says which side of halfway you are on, and every digit further right is too small to change that.'),
          warning('So do not round in stages. Rounding 2.348 to the nearest hundredth gives 2.35, and rounding *that* to the nearest tenth gives 2.4 — but 2.348 is genuinely nearer to 2.3. The 8 was allowed to push the 4, and it should never have had a say.'),
        ],
      },
      visual: {
        title: 'Which digit decides',
        blocks: [
          table(
            ['Round 5.2716 to…', 'Keep up to', 'Deciding digit', 'Answer'],
            [
              ['the nearest whole', '5', '2', '5'],
              ['the nearest tenth', '5.2', '7', '5.3'],
              ['the nearest hundredth', '5.27', '1', '5.27'],
              ['the nearest thousandth', '5.271', '6', '5.272'],
            ],
          ),
          text('Read the third column downwards. Each time, the deciding digit is the very next one along — never two along, and never the last digit of the number.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Round 14.0862 to the nearest tenth, and then to the nearest hundredth.'),
          steps([
            { label: 'To the nearest tenth: keep one place after the point.', latex: '14.0\\,|\\,862' },
            { label: 'The deciding digit is the 8, which is 5 or more.', latex: '8 \\geq 5' },
            { label: 'Round the tenths digit up.', latex: '14.0862 \\approx 14.1' },
            { label: 'Start again from the original for the hundredth.', latex: '14.08\\,|\\,62', note: 'Not from 14.1 — that answer has already lost the digits you need.' },
            { label: 'The deciding digit is 6, so round up.', latex: '14.0862 \\approx 14.09' },
          ]),
          tip('Always go back to the number you were given. Every rounding question is answered from the original, never from an earlier answer.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('round-decimals', 4)] },
      independent: { title: 'On your own', blocks: [practice('round-decimals', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('round-decimals', 3, 'Rounding where the digit to round is a 9.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Find the place you are rounding to, then look at the single digit on its right.',
            '5 or more rounds up; less than 5 leaves the digit alone.',
            'Round once, from the original number.',
            'A 9 rounded up carries, exactly as it does in addition: 3.97 to the nearest tenth is 4.0.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'multiply-decimals-01',
    unitId: 'unit-decimal-operations-5',
    conceptId: 'multiply-decimals',
    title: 'Multiplying Decimals',
    summary: 'Multiply as though the points were not there, then count them back in — and expect an answer that may be smaller than both factors.',
    objectives: [
      'Multiply two decimals using whole-number multiplication',
      'Count decimal places to place the point',
      'Estimate first, to catch a point in the wrong place',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'One multiplication you already know',
        blocks: [
          text('You can work out 24 × 3. That is the whole of 0.24 × 0.3, apart from one question: where does the point go?'),
          math('24 \\times 3 = 72 \\quad\\Longrightarrow\\quad 0.24 \\times 0.3 = 0.072'),
          text('This lesson is about answering that question by reasoning rather than by hoping.'),
        ],
      },
      intuition: {
        title: 'Where the places come from',
        blocks: [
          text('Use what you know about powers of ten. 0.24 is 24 divided by 100, and 0.3 is 3 divided by 10.'),
          math('0.24 \\times 0.3 = \\frac{24}{100} \\times \\frac{3}{10} = \\frac{72}{1000} = 0.072'),
          text('The two denominators multiplied: a hundred times a ten is a thousand. So the answer needs three decimal places — two from the first factor, one from the second.'),
          insight('Add the decimal places of both factors. Tenths times tenths give hundredths, because each factor contributed one place. This is why the rule is a sum and not a maximum.'),
          warning('Multiplying is not adding. Lining up the decimal points under one another is the rule for column addition, and it gives the wrong place here every time the factors have different lengths.'),
          text('There is a second surprise. Both factors are less than one, and the answer is smaller than both of them. Nothing has gone wrong: 0.3 of something is less than that something, and 0.24 is a small something to begin with.'),
        ],
      },
      visual: {
        title: 'Seeing a decimal product',
        blocks: [
          text('Take a square and call it one. Shade six tenths across it, and four tenths down it.'),
          figure({ kind: 'area-model', rows: 10, columns: 10, shadedRows: 4, shadedColumns: 6 }, '0.6 × 0.4: the overlap is 24 of the 100 small squares, which is 0.24.'),
          text('The grid has a hundred small squares because each side was cut into ten. Twenty-four of them are shaded both ways, so the product is 24 hundredths.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Work out 3.4 × 1.25.'),
          steps([
            { label: 'Estimate first, so a misplaced point cannot survive.', latex: '3 \\times 1 = 3', note: 'The answer will be a little above 3.' },
            { label: 'Ignore the points and multiply as whole numbers.', latex: '34 \\times 125 = 4250' },
            { label: 'Count the decimal places in each factor.', latex: '3.4 \\rightarrow 1, \\quad 1.25 \\rightarrow 2' },
            { label: 'Add them.', latex: '1 + 2 = 3' },
            { label: 'Put three places into the answer.', latex: '4.250 = 4.25' },
            { label: 'Check against the estimate.', latex: '4.25 \\approx 3', note: 'The right size. 42.5 or 0.425 would not have been.' },
          ]),
          tip('A trailing zero can be dropped after the point is placed, but not before: 4250 needs all four digits while you are counting places.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('multiply-decimals', 4)] },
      independent: { title: 'On your own', blocks: [practice('multiply-decimals', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('multiply-decimals', 3, 'Decide where the point goes without doing the multiplication.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Multiply as if both numbers were whole.',
            'Count the decimal places in both factors and add them.',
            'That total is how many places the answer has.',
            'Estimate first. The digits are rarely wrong; the point often is.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'divide-decimals-01',
    unitId: 'unit-decimal-operations-5',
    conceptId: 'divide-decimals',
    title: 'Dividing Decimals',
    summary: 'Shift both numbers until the divisor is a whole number — the answer does not notice.',
    objectives: [
      'Divide a decimal by a whole number',
      'Divide by a decimal by shifting both numbers',
      'Explain why shifting both leaves the quotient unchanged',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Long division with one extra worry',
        blocks: [
          text('7.2 ÷ 3 is not hard: it is 72 tenths shared between 3, which is 24 tenths, or 2.4. Dividing a decimal by a whole number is ordinary long division with the point carried straight up.'),
          text('The awkward case is dividing *by* a decimal. What does 7.2 ÷ 0.3 even mean? You cannot share something between three tenths of a person.'),
        ],
      },
      intuition: {
        title: 'Division is a fraction, and a fraction can be rescaled',
        blocks: [
          text('Read 7.2 ÷ 0.3 as "how many 0.3s fit inside 7.2?". That question has an answer, and it is larger than 7.2 — because 0.3 is small, so a lot of them fit.'),
          math('7.2 \\div 0.3 = \\frac{7.2}{0.3} = \\frac{72}{3} = 24'),
          text('The middle step multiplied the top and the bottom by 10. That is exactly what you do to a fraction to find an equivalent one, and it changes the name of the division without changing its value.'),
          insight('Twelve cakes shared between four people, and a hundred and twenty cakes shared between forty people, give each person the same amount. Scaling both numbers by the same factor never changes a quotient.'),
          warning('Both numbers, or neither. Moving the point in 0.3 to make it 3 while leaving 7.2 alone turns the answer into 2.4 — a tenth of the truth.'),
        ],
      },
      visual: {
        title: 'How many fit',
        blocks: [
          text('A strip 2 units long, cut into pieces 0.25 long. Counting the pieces is the division.'),
          figure({ kind: 'number-line', from: 0, to: 2, step: 0.25 }, '2 ÷ 0.25 = 8: count the gaps, not the marks — eight pieces of 0.25 fit into 2.'),
          text('The answer, 8, is larger than the 2 you started with. That is not a mistake — it is what happens whenever you divide by a number below one.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Work out 9.36 ÷ 1.2.'),
          steps([
            { label: 'Estimate. 1.2 is about 1, so the answer is about 9.', latex: '9.36 \\div 1.2 \\approx 9' },
            { label: 'The divisor has one decimal place, so shift both numbers one place.', latex: '\\frac{9.36}{1.2} = \\frac{93.6}{12}' },
            { label: 'Now it is ordinary long division. The point goes straight up.', latex: '93.6 \\div 12' },
            { label: '12 into 93 goes 7, remainder 9.', latex: '7' },
            { label: 'Bring down the 6: 12 into 96 goes 8.', latex: '7.8' },
            { label: 'Check against the estimate.', latex: '7.8 \\approx 9' },
          ]),
          tip('Multiply back to check: 7.8 × 1.2 should return 9.36. A division checked by a multiplication is a division you can trust.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('divide-decimals', 4)] },
      independent: { title: 'On your own', blocks: [practice('divide-decimals', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('divide-decimals', 3, 'Decide whether the answer will be bigger or smaller than the number you started with.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Dividing by a whole number: carry the point straight up.',
            'Dividing by a decimal: shift both numbers until the divisor is whole.',
            'Both numbers move the same number of places, or the answer changes.',
            'Dividing by a number below 1 makes the answer bigger.',
          ]),
        ],
      },
    },
  }),

  /* ------------------------------------------------------------ fractions */
  lesson({
    id: 'fraction-as-division-01',
    unitId: 'unit-fractions-arithmetic',
    conceptId: 'fraction-as-division',
    title: 'A Fraction Is a Division',
    summary: 'The bar has always been a division sign — which is what finally makes a remainder something you can write down exactly.',
    objectives: [
      'Read a/b as a ÷ b',
      'Write the answer to a whole-number division as a fraction or mixed number',
      'Interpret a remainder in the context of a problem',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'The question remainders left open',
        blocks: [
          text('Three pizzas shared between four children. Long division says 3 ÷ 4 is "0 remainder 3", which is true and useless: nobody has eaten anything.'),
          text('But you can answer it. Cut each pizza into quarters, and there are twelve quarters for four children — three each.'),
          math('3 \\div 4 = \\frac{3}{4}'),
          text('The fraction *is* the answer to the division. It always was.'),
        ],
      },
      intuition: {
        title: 'Why the bar means divide',
        blocks: [
          text('Look at what 3/4 means: one whole cut into 4 equal parts, and 3 of those parts taken. Now look at what 3 ÷ 4 means: 3 wholes shared into 4 equal parts.'),
          figure(
            {
              kind: 'fraction-bars',
              bars: [
                { parts: 4, shaded: 4, label: 'pizza 1' },
                { parts: 4, shaded: 4, label: 'pizza 2' },
                { parts: 4, shaded: 4, label: 'pizza 3' },
              ],
            },
            'Twelve quarters between four children: each child gets three quarters.',
          ),
          insight('These are the same picture read two ways, which is why one symbol serves for both. Every fraction is a division waiting to be done, and every division has a fraction as its exact answer.'),
          text('This also settles what to do with a remainder. 17 ÷ 5 is 3 remainder 2, and those 2 left over are 2 fifths of another whole.'),
          math('17 \\div 5 = 3\\tfrac{2}{5}'),
          warning('The remainder becomes the *numerator*, and the number you divided by stays underneath. 3 5/2 is not a number anyone means: the pieces would be bigger than the whole.'),
        ],
      },
      visual: {
        title: 'The remainder is the numerator',
        blocks: [
          table(
            ['Division', 'With a remainder', 'As a mixed number'],
            [
              ['7 ÷ 2', '3 remainder 1', '3 1/2'],
              ['17 ÷ 5', '3 remainder 2', '3 2/5'],
              ['23 ÷ 4', '5 remainder 3', '5 3/4'],
              ['9 ÷ 10', '0 remainder 9', '9/10'],
            ],
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Twenty-three metres of rope are cut into 4 equal lengths. How long is each?'),
          steps([
            { label: 'Write the sharing as a division.', latex: '23 \\div 4' },
            { label: 'Do the division, keeping the remainder.', latex: '23 \\div 4 = 5 \\text{ remainder } 3' },
            { label: 'The 3 left over is 3 quarters of a metre.', latex: '\\frac{3}{4}' },
            { label: 'Write the exact answer.', latex: '5\\tfrac{3}{4} \\text{ m}' },
          ]),
          tip('Ask what the remainder is a remainder *of*. Three metres left to share between four cuts is three quarters each; three children left over when filling a minibus is a whole extra minibus. The arithmetic is the same and the answers are not.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('fraction-as-division', 4)] },
      independent: { title: 'On your own', blocks: [practice('fraction-as-division', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('fraction-as-division', 3, 'Decide what the remainder means before you answer.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'a/b means a ÷ b, in every context.',
            'The remainder becomes the numerator; the divisor stays the denominator.',
            'Every whole-number division has an exact fractional answer.',
            'What to do with a leftover depends on what is being shared.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'add-mixed-numbers-01',
    unitId: 'unit-fractions-arithmetic',
    conceptId: 'add-mixed-numbers',
    title: 'Adding and Subtracting Mixed Numbers',
    summary: 'Wholes with wholes, parts with parts — until the parts overflow or run short, and a whole has to be traded.',
    objectives: [
      'Add mixed numbers with unlike denominators',
      'Regroup when the fraction part is one or more',
      'Subtract by borrowing a whole and turning it into fraction pieces',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Column arithmetic, with pieces instead of tens',
        blocks: [
          text('2 1/4 + 1 1/2 is two calculations sitting side by side. Add the wholes: 2 + 1 = 3. Add the parts: 1/4 + 1/2 = 3/4. The answer is 3 3/4.'),
          text('That works whenever the parts behave. This lesson is about the two cases where they do not — the parts adding to more than a whole, and a subtraction where the part being taken away is the bigger one.'),
        ],
      },
      intuition: {
        title: 'A whole is just a lot of pieces',
        blocks: [
          text('Add 2 3/5 and 1 4/5. The wholes give 3 and the parts give 7/5 — but 7/5 is more than one whole, so 3 7/5 is a value that is right and a form that is not finished.'),
          math('\\frac{7}{5} = \\frac{5}{5} + \\frac{2}{5} = 1\\tfrac{2}{5}'),
          math('3\\tfrac{7}{5} = 4\\tfrac{2}{5}'),
          insight('This is carrying. In column addition, ten ones become one ten and move left. Here, five fifths become one whole and move left. The same idea, with the size of the group chosen by the denominator instead of by our number system.'),
          text('Subtraction has the mirror problem. 5 1/4 − 2 3/4 asks you to take three quarters from one quarter, and there are not enough.'),
          warning('You cannot swap them and take 1/4 from 3/4. Column subtraction has the same temptation — taking 3 from 7 when the sum says 3 − 7 — and it is wrong for the same reason: it answers a different question.'),
          text('Borrow instead. Take one of the 5 wholes and turn it into quarters.'),
          math('5\\tfrac{1}{4} = 4 + 1 + \\tfrac{1}{4} = 4 + \\tfrac{4}{4} + \\tfrac{1}{4} = 4\\tfrac{5}{4}'),
          math('4\\tfrac{5}{4} - 2\\tfrac{3}{4} = 2\\tfrac{2}{4} = 2\\tfrac{1}{2}'),
        ],
      },
      visual: {
        title: 'Seeing the trade',
        blocks: [
          figure(
            {
              kind: 'fraction-bars',
              bars: [
                { parts: 5, shaded: 5, label: 'whole' },
                { parts: 5, shaded: 5, label: 'whole' },
                { parts: 5, shaded: 5, label: 'whole' },
                { parts: 5, shaded: 5, label: 'from 7/5' },
                { parts: 5, shaded: 2, label: '2/5 left' },
              ],
            },
            'Three wholes and seven fifths: five of those fifths fill a fourth bar, leaving two.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Work out 3 5/6 + 2 3/4, and then 6 1/3 − 2 5/6.'),
          steps([
            { label: 'Different denominators, so find a common one.', latex: '\\operatorname{lcm}(6, 4) = 12' },
            { label: 'Rewrite both fraction parts.', latex: '\\tfrac{5}{6} = \\tfrac{10}{12}, \\quad \\tfrac{3}{4} = \\tfrac{9}{12}' },
            { label: 'Add wholes and parts separately.', latex: '5 + \\tfrac{19}{12}' },
            { label: 'The part is more than a whole, so trade.', latex: '\\tfrac{19}{12} = 1\\tfrac{7}{12}' },
            { label: 'Finish.', latex: '3\\tfrac{5}{6} + 2\\tfrac{3}{4} = 6\\tfrac{7}{12}' },
            { label: 'Now the subtraction, over sixths.', latex: '6\\tfrac{2}{6} - 2\\tfrac{5}{6}' },
            { label: '2/6 is not enough, so borrow a whole as sixths.', latex: '6\\tfrac{2}{6} = 5\\tfrac{8}{6}' },
            { label: 'Subtract wholes and parts.', latex: '5\\tfrac{8}{6} - 2\\tfrac{5}{6} = 3\\tfrac{3}{6} = 3\\tfrac{1}{2}' },
          ]),
          tip('Estimate before you start. 3 5/6 is nearly 4 and 2 3/4 is nearly 3, so the answer should be close to 7. An answer of 5 or of 12 is wrong before you check a single fraction.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('add-mixed-numbers', 4)] },
      independent: { title: 'On your own', blocks: [practice('add-mixed-numbers', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('add-mixed-numbers', 3, 'Subtractions that need a whole borrowed.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Give the fraction parts a common denominator first.',
            'Add or subtract the wholes and the parts separately.',
            'A fraction part of one or more is carried across as a whole.',
            'When there are not enough pieces to take away, borrow a whole and turn it into pieces.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'fraction-scaling-01',
    unitId: 'unit-fraction-scaling-5',
    conceptId: 'fraction-scaling',
    title: 'Multiplication as Scaling',
    summary: 'You can tell whether a product will be bigger or smaller than what you started with, without multiplying anything.',
    objectives: [
      'Predict the size of a product from the size of the multiplier',
      'Compare a product with its factor without calculating',
      'Explain why multiplying by 1 in disguise changes nothing',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'A question with no calculation in it',
        blocks: [
          text('Which is bigger: 847 × 3/4, or 847? You do not need the product. Three quarters of something is less than all of it, so the answer is 847.'),
          text('That is the whole lesson, and it is worth a lesson because the habit it replaces — multiply first, think afterwards — is what lets an answer of the wrong size go unnoticed.'),
        ],
      },
      intuition: {
        title: 'The multiplier is a stretch or a squash',
        blocks: [
          text('Stop reading × as "times" and read it as "of". Then a multiplier is an instruction about size.'),
          list([
            'Multiply by a number **greater than 1** and the result is **larger** than the starting number. It has been stretched.',
            'Multiply by a number **less than 1** and the result is **smaller**. It has been squashed.',
            'Multiply by **exactly 1** — including 1 in disguise, like 5/5 — and nothing changes at all.',
          ]),
          figure(
            { kind: 'tape-diagram', rows: [{ label: '1 × n', parts: 4 }, { label: '3/4 × n', parts: 3 }, { label: '3/2 × n', parts: 6 }], unitLabel: 'quarters of n' },
            'The same n, scaled by three quarters and by three halves.',
          ),
          insight('This is why 4/4 × 7 = 7 without any work. Multiplying by a fraction whose numerator and denominator match is multiplying by 1 — which is exactly the move that makes equivalent fractions, seen from the other side.'),
          warning('"Multiplication makes things bigger" was never a rule about multiplication. It was a rule about whole numbers greater than 1, which is all you had met until now.'),
        ],
      },
      visual: {
        title: 'Comparing without computing',
        blocks: [
          table(
            ['Product', 'Multiplier', 'Compared with 24', 'Why'],
            [
              ['24 × 7/8', '7/8, less than 1', 'smaller', 'seven eighths of it'],
              ['24 × 8/8', '1', 'equal', 'all of it'],
              ['24 × 9/8', '9/8, more than 1', 'larger', 'all of it and an eighth more'],
              ['24 × 5/3', '5/3, more than 1', 'larger', 'more than one and a half times'],
            ],
          ),
          text('Only the second column ever matters. Compare the multiplier with 1, and the comparison is decided.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Without multiplying, say whether each product is less than, equal to, or greater than 36.'),
          steps([
            { label: '36 × 2/5. Is 2/5 above or below 1?', latex: '\\tfrac{2}{5} < 1', note: 'The product is less than 36.' },
            { label: '36 × 11/11.', latex: '\\tfrac{11}{11} = 1', note: 'The product is exactly 36.' },
            { label: '36 × 1 1/4.', latex: '1\\tfrac{1}{4} > 1', note: 'The product is greater than 36.' },
            { label: 'And a check on the last one, since it is quick.', latex: '36 \\times 1\\tfrac{1}{4} = 45' },
          ]),
          tip('Compare the numerator with the denominator. Numerator smaller means a multiplier below 1; equal means exactly 1; larger means above 1.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('fraction-scaling', 4)] },
      independent: { title: 'On your own', blocks: [practice('fraction-scaling', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('fraction-scaling', 3, 'Compare two products without working either of them out.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Read × as "of", and the multiplier tells you the size of the answer.',
            'Multiplier above 1: bigger. Below 1: smaller. Exactly 1: unchanged.',
            'A fraction with matching top and bottom is 1 in disguise.',
            'Predict the size first; it is the cheapest check you will ever run.',
          ]),
          insight('In Grade 7 this becomes a scale factor, and in Algebra I it becomes the a in y = ax. The stretching and squashing never stops being the same idea.'),
        ],
      },
    },
  }),

  lesson({
    id: 'divide-unit-fractions-01',
    unitId: 'unit-fraction-scaling-5',
    conceptId: 'divide-unit-fractions',
    title: 'Dividing With Unit Fractions',
    summary: 'How many quarters fit in 3? Twelve — which is why dividing can make a number bigger.',
    objectives: [
      'Divide a whole number by a unit fraction',
      'Divide a unit fraction by a whole number',
      'Check a fraction division by multiplying back',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Two questions that look alike and are not',
        blocks: [
          text('4 ÷ 1/2 and 1/2 ÷ 4 use the same two numbers, and their answers are 8 and 1/8. Getting them the wrong way round is the commonest error in this whole topic, and it comes from not saying out loud what each one asks.'),
        ],
      },
      intuition: {
        title: 'Say the question in words',
        blocks: [
          text('**4 ÷ 1/2** asks: how many halves fit inside 4? Each whole holds two halves, and there are four wholes, so eight.'),
          math('4 \\div \\tfrac{1}{2} = 8'),
          text('**1/2 ÷ 4** asks: half a thing shared between 4. Each share is an eighth.'),
          math('\\tfrac{1}{2} \\div 4 = \\tfrac{1}{8}'),
          insight('Dividing by a fraction below 1 makes the answer bigger, because you are counting how many small things fit into a large one. Dividing a fraction by a whole number makes it smaller, because you are cutting it up further. The direction of the answer is decided by which number is the small one.'),
          warning('"Division makes things smaller" is the twin of "multiplication makes things bigger", and it fails for the same reason: it was a rule about whole numbers, and the numbers have changed.'),
        ],
      },
      visual: {
        title: 'Counting the pieces that fit',
        blocks: [
          figure(
            {
              kind: 'fraction-bars',
              bars: [
                { parts: 4, shaded: 4, label: 'whole' },
                { parts: 4, shaded: 4, label: 'whole' },
                { parts: 4, shaded: 4, label: 'whole' },
              ],
            },
            '3 ÷ 1/4: three wholes, each cut into quarters. Count the quarters — there are 12.',
          ),
          text('The picture also shows the multiplication hiding underneath. Three wholes, four quarters in each: 3 × 4 = 12.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A 5-metre ribbon is cut into pieces 1/3 of a metre long. How many pieces? Then: 1/6 of a litre of paint is shared equally between 3 pots. How much in each?'),
          steps([
            { label: 'The first question counts how many thirds fit in 5.', latex: '5 \\div \\tfrac{1}{3}' },
            { label: 'Each metre holds 3 thirds.', latex: '5 \\times 3 = 15', note: '15 pieces — more than 5, because the pieces are small.' },
            { label: 'The second question shares a sixth between 3.', latex: '\\tfrac{1}{6} \\div 3' },
            { label: 'Cutting each sixth into 3 makes eighteenths.', latex: '\\tfrac{1}{6} \\div 3 = \\tfrac{1}{18}' },
            { label: 'Check by multiplying back.', latex: '\\tfrac{1}{18} \\times 3 = \\tfrac{3}{18} = \\tfrac{1}{6}' },
          ]),
          tip('Every division can be checked with a multiplication. If your answer times the divisor does not return the number you started with, the answer is wrong — and this is the topic where that check earns its keep.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('divide-unit-fractions', 4)] },
      independent: { title: 'On your own', blocks: [practice('divide-unit-fractions', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('divide-unit-fractions', 3, 'Decide which of the two divisions the situation is asking for.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Whole ÷ unit fraction: how many small pieces fit? The answer is bigger.',
            'Unit fraction ÷ whole: cut the piece up further. The answer is smaller.',
            'Say the question in words before choosing an operation.',
            'Check by multiplying back.',
          ]),
          insight('Grade 6 turns this into "multiply by the reciprocal". You have just seen why that rule is true, which is a better place to meet it from than the rule itself.'),
        ],
      },
    },
  }),

  /* -------------------------------------------------------------- algebra */
  lesson({
    id: 'numerical-expressions-01',
    unitId: 'unit-intro-algebra',
    conceptId: 'numerical-expressions',
    title: 'Writing and Reading Expressions',
    summary: 'Brackets are punctuation. They say which part of a calculation is meant to be one thought.',
    objectives: [
      'Evaluate an expression containing brackets',
      'Write an expression for a sentence in words',
      'Compare two expressions without evaluating them',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'The same words, two meanings',
        blocks: [
          text('"Add 4 and 2, then multiply by 3." "Multiply 2 by 3, then add 4." Both use 4, 2 and 3, and one of them gives 18 while the other gives 10.'),
          math('3 \\times (4 + 2) = 18 \\qquad 4 + 2 \\times 3 = 10'),
          text('The brackets are the only thing separating them. They are not decoration and they are not optional.'),
        ],
      },
      intuition: {
        title: 'Brackets outrank everything',
        blocks: [
          text('The order of operations settles what to do when there is no instruction: multiplication and division before addition and subtraction. Brackets are the instruction that overrides it.'),
          insight('Think of brackets as a box drawn around a piece of the calculation, with a note saying "work this out first, and treat the result as a single number". Everything outside the box waits.'),
          text('This also lets you read an expression without computing it. In 6 × (13 + 9), the bracket is one number that gets multiplied by 6. You need not know it is 22 to know the whole thing is six times something.'),
          warning('3 × (4 + 2) is not 3 × 4 + 2. Dropping the brackets and carrying on with the usual precedence answers a different question — the exact one the brackets were written to prevent.'),
        ],
      },
      visual: {
        title: 'From words to symbols',
        blocks: [
          table(
            ['In words', 'As an expression', 'Value'],
            [
              ['add 5 and 3, then double', '2 × (5 + 3)', '16'],
              ['double 5, then add 3', '2 × 5 + 3', '13'],
              ['subtract 7 from 20, then halve', '(20 − 7) ÷ 2', '6.5'],
              ['three times the sum of 6 and 4', '3 × (6 + 4)', '30'],
              ['three times 6, plus 4', '3 × 6 + 4', '22'],
            ],
          ),
          text('The signal in the words is usually a comma or the word "sum". "Three times the sum of 6 and 4" bundles the 6 and the 4 together before anything else happens to them.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Evaluate 40 − 3 × (7 + 2), and then write an expression for "subtract 4 from 15, then multiply by 6".'),
          steps([
            { label: 'Brackets first.', latex: '7 + 2 = 9' },
            { label: 'The expression is now bracket-free.', latex: '40 - 3 \\times 9' },
            { label: 'Multiplication before subtraction.', latex: '3 \\times 9 = 27' },
            { label: 'Subtract.', latex: '40 - 27 = 13' },
            { label: 'For the sentence: the subtraction happens first, so it goes in brackets.', latex: '(15 - 4) \\times 6' },
            { label: 'Check.', latex: '11 \\times 6 = 66', note: 'Without the brackets it would be 15 − 24, which is not what the words say.' },
          ]),
          tip('Work outwards from the innermost bracket, and rewrite the whole expression after every step. Most errors here are bookkeeping errors, not arithmetic ones.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('numerical-expressions', 4)] },
      independent: { title: 'On your own', blocks: [practice('numerical-expressions', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('numerical-expressions', 3, 'Compare two expressions without working either of them out.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Brackets happen first, whatever the other rules would say.',
            'A bracket is one number as far as the rest of the expression is concerned.',
            '"The sum of" and a comma in the words usually mean brackets in the symbols.',
            'You can often compare two expressions without evaluating either.',
          ]),
          insight('Next comes the same sentence with a letter in it: 3 × (x + 2). Nothing about the brackets changes — only that you no longer know what is inside.'),
        ],
      },
    },
  }),

  lesson({
    id: 'number-pattern-pairs-01',
    unitId: 'unit-coordinate-plane-5',
    conceptId: 'number-pattern-pairs',
    title: 'Two Patterns at Once',
    summary: 'Run two rules side by side, and a relationship appears between them that neither rule mentions.',
    objectives: [
      'Generate two sequences from two rules',
      'Describe how corresponding terms are related',
      'Write the pairs of terms as ordered pairs',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'A relationship nobody wrote down',
        blocks: [
          text('Start two sequences at 0. The first adds 2 each time; the second adds 6.'),
          table(
            ['Step', '0', '1', '2', '3', '4', '5'],
            [
              ['Rule: add 2', '0', '2', '4', '6', '8', '10'],
              ['Rule: add 6', '0', '6', '12', '18', '24', '30'],
            ],
          ),
          text('Neither rule says anything about the other. But look down the columns: every number on the second row is three times the number above it. That relationship was never stated — it appeared.'),
        ],
      },
      intuition: {
        title: 'Why it appears',
        blocks: [
          text('At every step, the second sequence gains 6 while the first gains 2. It is gaining three times as fast, and it started level, so it stays three times ahead.'),
          figure({ kind: 'tape-diagram', rows: [{ label: 'add 2', parts: 2 }, { label: 'add 6', parts: 6 }], unitLabel: 'one step' }, 'One step of each rule, drawn to the same scale.'),
          insight('The multiplier between the sequences is the multiplier between their step sizes: 6 ÷ 2 = 3. That is only true because both started at 0 — a head start on one of them would break it, and finding out how is worth an experiment.'),
          text('The pairs of corresponding terms can be written together: (0, 0), (2, 6), (4, 12), (6, 18). A pair like that is the raw material of a graph, and the next lesson plots them.'),
          warning('Check a rule against every term, not the first two. "Add 2" and "double" agree at 2 and 4, and part company immediately afterwards.'),
        ],
      },
      visual: {
        title: 'Two rules, one table',
        blocks: [
          table(
            ['First rule', 'Second rule', 'Relationship', 'Ordered pairs'],
            [
              ['add 3', 'add 9', 'second is 3 × first', '(3, 9), (6, 18), (9, 27)'],
              ['add 5', 'add 10', 'second is 2 × first', '(5, 10), (10, 20), (15, 30)'],
              ['add 4', 'add 4', 'they are equal', '(4, 4), (8, 8), (12, 12)'],
              ['add 10', 'add 5', 'second is half the first', '(10, 5), (20, 10), (30, 15)'],
            ],
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Sequence A starts at 0 and adds 4. Sequence B starts at 0 and adds 12. Find the 5th term of each, and say how they are related.'),
          steps([
            { label: 'Generate A.', latex: '0, 4, 8, 12, 16, 20' },
            { label: 'Generate B.', latex: '0, 12, 24, 36, 48, 60' },
            { label: 'The 5th terms.', latex: 'A_5 = 20, \\quad B_5 = 60' },
            { label: 'Compare the step sizes.', latex: '12 \\div 4 = 3' },
            { label: 'So every term of B is three times its partner in A.', latex: '60 = 3 \\times 20' },
            { label: 'As an ordered pair.', latex: '(20,\\ 60)' },
          ]),
          tip('If the two sequences do not both start at 0, the neat multiplier disappears — but a relationship is still there. Look for what you would have to do to every term of one to get the other.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('number-pattern-pairs', 4)] },
      independent: { title: 'On your own', blocks: [practice('number-pattern-pairs', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('number-pattern-pairs', 3, 'Find the rule that produced a pair of sequences.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Two rules run side by side produce a relationship neither rule states.',
            'When both start at 0, the ratio between terms is the ratio between step sizes.',
            'Corresponding terms can be written as ordered pairs.',
            'Test any rule you find against every term you were given.',
          ]),
        ],
      },
    },
  }),

  /* ------------------------------------------------------------- geometry */
  lesson({
    id: 'coordinate-first-quadrant-01',
    unitId: 'unit-coordinate-plane-5',
    conceptId: 'coordinate-first-quadrant',
    title: 'Plotting Points',
    summary: 'Two number lines at right angles give every point on the page a two-part name — and the order of the parts matters.',
    objectives: [
      'Plot a point from an ordered pair',
      'Read the coordinates of a plotted point',
      'Graph a pair of related sequences and describe the shape',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Naming a place with two numbers',
        blocks: [
          text('One number line names points along a line. To name a point on a page you need two: how far across, and how far up.'),
          text('The two lines are called axes. The one across is the x-axis, the one up is the y-axis, and they cross at a point called the origin, where both are 0.'),
        ],
      },
      intuition: {
        title: 'Across before up',
        blocks: [
          text('An ordered pair is written (x, y). The first number is always the across, the second is always the up.'),
          insight('Along the corridor, then up the stairs. The order is a convention, not a discovery — but it is a convention everyone keeps, so breaking it puts your point somewhere else entirely.'),
          text('(3, 5) and (5, 3) are different points. Only when the two numbers happen to be equal do they land in the same place.'),
          figure(
            { kind: 'coordinate-grid', min: 0, max: 8, points: [{ x: 3, y: 5, label: '(3, 5)' }, { x: 5, y: 3, label: '(5, 3)' }] },
            'Two points that use the same digits and are nowhere near each other.',
          ),
          warning('Count the *units* on the axis, not the lines on the grid. If the grid is drawn in twos, three squares across is 6, not 3.'),
        ],
      },
      visual: {
        title: 'A pattern, plotted',
        blocks: [
          text('The pairs from the last lesson — one sequence adding 1, the other adding 3 — plotted as points.'),
          figure(
            {
              kind: 'coordinate-grid',
              min: 0,
              max: 8,
              points: [{ x: 0, y: 0 }, { x: 1, y: 3 }, { x: 2, y: 6 }],
            },
            '(0, 0), (1, 3), (2, 6): the points climb three for every one across, and they lie in a straight line.',
          ),
          text('That straightness is not a coincidence. Every step adds the same amount to each coordinate, so every step moves the same distance in the same direction.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Plot (4, 2) and (2, 7). Then read the point that sits 6 across and 6 up.'),
          steps([
            { label: 'For (4, 2), start at the origin and go 4 along the x-axis.', latex: 'x = 4' },
            { label: 'From there, go 2 up.', latex: 'y = 2' },
            { label: 'For (2, 7): 2 across, then 7 up.', latex: '(2,\\ 7)', note: 'Nearly the reverse of the first point, and far away from it.' },
            { label: 'Reading backwards: 6 across and 6 up.', latex: '(6,\\ 6)' },
          ]),
          figure({ kind: 'coordinate-grid', min: 0, max: 8, points: [{ x: 4, y: 2, label: 'A' }, { x: 2, y: 7, label: 'B' }, { x: 6, y: 6, label: 'C' }] }),
          tip('When you read a point off a grid, say the across number out loud first. Most reversed coordinates are read correctly and written down in the wrong order.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('coordinate-first-quadrant', 4)] },
      independent: { title: 'On your own', blocks: [practice('coordinate-first-quadrant', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('coordinate-first-quadrant', 3, 'Find the missing corner of a rectangle from three of its vertices.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The axes cross at the origin, (0, 0).',
            'An ordered pair is (across, up) — x first, then y.',
            '(3, 5) and (5, 3) are different points.',
            'Points from a constant-step pattern lie in a straight line.',
          ]),
          insight('Grade 6 adds the negative halves of both axes and three more quadrants. Nothing you have learned here changes; there is simply more page.'),
        ],
      },
    },
  }),

  lesson({
    id: 'volume-unit-cubes-01',
    unitId: 'unit-volume-5',
    conceptId: 'volume-unit-cubes',
    title: 'Volume by Counting Cubes',
    summary: 'Fill the box with unit cubes and count them. The formula is a shortcut for that count, not a rule to memorise.',
    objectives: [
      'Find a volume by counting unit cubes',
      'Explain why volume is base area times height',
      'Use cubic units, and say what makes them cubic',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Covering, then filling',
        blocks: [
          text('Area asks how much surface there is to cover, and it is measured by counting unit squares. Volume asks how much space there is to fill, and it is measured the same way — by counting unit cubes.'),
          text('A unit cube is 1 cm along every edge. Its volume is 1 cubic centimetre, written cm³. The little 3 is there because three measurements went into it.'),
        ],
      },
      intuition: {
        title: 'One layer at a time',
        blocks: [
          text('Lay cubes across the bottom of a box that is 5 cm by 3 cm. The bottom holds 5 × 3 = 15 cubes, and that number is exactly the area of the base.'),
          figure({ kind: 'grid', width: 5, height: 3 }, 'The bottom layer seen from above: 15 cubes.'),
          text('Now stack layers. A box 4 cm tall holds 4 identical layers, so 4 × 15 = 60 cubes fit.'),
          figure(
            { kind: 'prism', length: 5, width: 3, height: 4, labels: { length: '5 cm', width: '3 cm', height: '4 cm' } },
            'Four layers of fifteen: 60 cubic centimetres.',
          ),
          insight('Volume is base area × height. The three-number formula is that sentence written out — and because any face can be the base, the three numbers can be multiplied in any order. A box laid on its side holds the same amount.'),
          warning('Adding the three edges gives 5 + 3 + 4 = 12, which is a length. Space is not measured in lengths. If your units are cm rather than cm³, you have answered a different question.'),
        ],
      },
      visual: {
        title: 'What the little 3 records',
        blocks: [
          table(
            ['Measurement', 'What is counted', 'Unit'],
            [
              ['length', 'unit lengths in a row', 'cm'],
              ['area', 'unit squares over a surface', 'cm²'],
              ['volume', 'unit cubes filling a space', 'cm³'],
            ],
          ),
          text('The exponent counts how many directions were measured. That is all it has ever meant here.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A crate is 6 cm long, 4 cm wide and 7 cm tall. How many centimetre cubes fill it? Then: another crate holds 96 cm³ on a base of 6 cm by 4 cm. How tall is it?'),
          steps([
            { label: 'One layer is the base area.', latex: '6 \\times 4 = 24' },
            { label: 'There are 7 such layers.', latex: '24 \\times 7 = 168' },
            { label: 'Answer, in cubic units.', latex: '168 \\text{ cm}^3' },
            { label: 'For the second crate, start from the same base area.', latex: '6 \\times 4 = 24' },
            { label: 'How many layers of 24 make 96?', latex: '96 \\div 24 = 4' },
            { label: 'The crate is 4 cm tall.', latex: '4 \\text{ cm}', note: 'A height is a length, so this answer is not cubed.' },
          ]),
          tip('Say what you are counting before you write the unit. Cubes give cm³, squares give cm², and steps along an edge give cm.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('volume-unit-cubes', 4)] },
      independent: { title: 'On your own', blocks: [practice('volume-unit-cubes', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('volume-unit-cubes', 3, 'Find a missing edge from the volume.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Volume counts the unit cubes that fill a solid.',
            'One layer holds length × width cubes; there are height layers.',
            'Volume = base area × height = length × width × height.',
            'Units are cubed, because three measurements were multiplied.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'volume-composite-01',
    unitId: 'unit-volume-5',
    conceptId: 'volume-composite',
    title: 'Volume of Combined Solids',
    summary: 'A solid made from two boxes holds what the two boxes hold — provided no cube is counted twice.',
    objectives: [
      'Find the volume of a solid made from two rectangular prisms',
      'Choose where to cut a composite solid',
      'Find a volume by subtracting a missing piece',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Real objects are rarely one box',
        blocks: [
          text('A step, an L-shaped planter, a house with a chimney: none of them is a rectangular prism, and all of them are made of rectangular prisms.'),
          text('There is no new formula for these. There is a decision — where to cut — and then the volume you already know how to find, twice.'),
        ],
      },
      intuition: {
        title: 'Cut it, or complete it',
        blocks: [
          text('Two methods work, and both are worth having.'),
          list([
            '**Cut.** Slice the solid into rectangular prisms that do not overlap, find each volume, and add them.',
            '**Complete.** Imagine the smallest box that contains the whole solid, find its volume, and take away the piece that is missing.',
          ]),
          insight('Both are the same statement: volume is additive. Cubes do not merge or vanish when two boxes are pushed together, so the total is the sum of the parts — as long as each cube is counted exactly once.'),
          warning('Multiplying the overall length by the overall width by the overall height gives the box the solid sits *inside*, not the solid. That answer is always too big, and by exactly the piece that is not there.'),
          text('Where you cut is free. A step cut vertically and the same step cut horizontally give two different pairs of prisms and the same total — which is a useful check when a question is awkward.'),
        ],
      },
      visual: {
        title: 'One solid, two boxes',
        blocks: [
          figure({ kind: 'prism', length: 6, width: 3, height: 2, labels: { length: '6 cm', width: '3 cm', height: '2 cm' } }, 'The lower box: 6 × 3 × 2 = 36 cm³.'),
          figure({ kind: 'prism', length: 2, width: 3, height: 4, labels: { length: '2 cm', width: '3 cm', height: '4 cm' } }, 'The upper box standing on it: 2 × 3 × 4 = 24 cm³.'),
          math('36 + 24 = 60 \\text{ cm}^3'),
          text('The box that would contain both is 6 × 3 × 6 = 108 cm³, which is a great deal more. The difference, 48 cm³, is the empty space beside the upper box.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('An L-shaped block is 10 cm long, 4 cm wide and 3 cm tall, with a piece 4 cm long, 4 cm wide and 3 cm tall cut out of one end. Find its volume two ways.'),
          steps([
            { label: 'Completing: the full box first.', latex: '10 \\times 4 \\times 3 = 120' },
            { label: 'The missing piece.', latex: '4 \\times 4 \\times 3 = 48' },
            { label: 'Subtract.', latex: '120 - 48 = 72 \\text{ cm}^3' },
            { label: 'Cutting instead: what is left is one prism.', latex: '(10 - 4) \\times 4 \\times 3' },
            { label: 'Which is the same answer.', latex: '6 \\times 4 \\times 3 = 72 \\text{ cm}^3' },
          ]),
          tip('Do it both ways when you can. Two methods agreeing is a stronger check than looking over one method twice.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('volume-composite', 4)] },
      independent: { title: 'On your own', blocks: [practice('volume-composite', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('volume-composite', 3, 'Find the volume of the piece that was removed.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Volume is additive: parts add up, and no cube belongs to two parts.',
            'Cut the solid into prisms, or complete it and subtract.',
            'The overall dimensions describe the surrounding box, not the solid.',
            'Where you cut is your choice; the total does not depend on it.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'classify-quadrilaterals-01',
    unitId: 'unit-classify-shapes-5',
    conceptId: 'classify-quadrilaterals',
    title: 'Classifying Shapes',
    summary: 'Shape families sit inside one another, and "is a" runs one way only.',
    objectives: [
      'Classify a quadrilateral by its properties',
      'Decide whether one shape family contains another',
      'Explain why a square is a rectangle and a rectangle is not a square',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'A question that sounds like a trick',
        blocks: [
          text('Is a square a rectangle? Most people say no, because a square looks like a square and a rectangle looks long. But the question is not about looks.'),
          text('A rectangle is a quadrilateral with four right angles. A square has four right angles. So a square is a rectangle — a particular one, where the sides happen to be equal.'),
        ],
      },
      intuition: {
        title: 'Families inside families',
        blocks: [
          text('Every shape name is a list of requirements. A shape belongs to the family if it meets the list, whatever else may also be true of it.'),
          table(
            ['Family', 'Requirements'],
            [
              ['quadrilateral', 'four straight sides'],
              ['trapezium', 'at least one pair of parallel sides'],
              ['parallelogram', 'both pairs of opposite sides parallel'],
              ['rectangle', 'a parallelogram with four right angles'],
              ['rhombus', 'a parallelogram with four equal sides'],
              ['square', 'four right angles and four equal sides'],
            ],
          ),
          text('Read down the list and the requirements accumulate. A square meets every line above it, so a square is a rhombus, a rectangle, a parallelogram, a trapezium and a quadrilateral, all at once.'),
          insight('Adding a requirement makes a family smaller, not larger. There are fewer rectangles than parallelograms, and fewer squares than rectangles — which is why the arrow only points one way.'),
          warning('So "every square is a rectangle" is true and "every rectangle is a square" is false. Reversing a statement like this is a habit worth breaking now: it is the same error that will call a converse true in geometry proof.'),
        ],
      },
      visual: {
        title: 'The same shape, several names',
        blocks: [
          figure({ kind: 'symmetry', shape: 'square', showLines: true, label: 'square' }, 'Four equal sides, four right angles, four lines of symmetry.'),
          figure({ kind: 'symmetry', shape: 'rectangle', showLines: true, label: 'rectangle' }, 'Four right angles, but only two lines of symmetry — the diagonals do not fold.'),
          figure({ kind: 'symmetry', shape: 'parallelogram', label: 'parallelogram' }, 'Opposite sides parallel, no right angles, no lines of symmetry at all.'),
          text('Lines of symmetry make good evidence. A shape claiming to be a rectangle but folding along its diagonal is a rhombus, or a square.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A quadrilateral has four equal sides and no right angles. Name every family it belongs to. Then decide: is every rhombus a parallelogram?'),
          steps([
            { label: 'Four equal sides means both pairs of opposite sides are parallel.', latex: '\\text{parallelogram}' },
            { label: 'Four equal sides in a parallelogram.', latex: '\\text{rhombus}' },
            { label: 'It has at least one pair of parallel sides.', latex: '\\text{trapezium}' },
            { label: 'No right angles, so not this one.', latex: '\\text{not a rectangle}', note: 'And therefore not a square either.' },
            { label: 'The second question: a rhombus meets every requirement for a parallelogram.', latex: '\\text{yes}' },
          ]),
          tip('Answer these by checking the requirements, one at a time, against the shape in front of you. A picture that "looks like" a rhombus proves nothing; four equal sides proves it.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('classify-quadrilaterals', 4)] },
      independent: { title: 'On your own', blocks: [practice('classify-quadrilaterals', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('classify-quadrilaterals', 3, 'Decide whether a statement about two families is true in both directions.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A shape belongs to a family if it meets that family\'s requirements.',
            'More requirements means a smaller family.',
            'Every square is a rectangle; not every rectangle is a square.',
            'Check properties, not appearance.',
          ]),
        ],
      },
    },
  }),
];
